import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useFacilities } from "@/context/FacilityContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ruler, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Intensity() {
  const { t } = useLang();
  const { facilities } = useFacilities();
  const [year, setYear] = useState(new Date().getFullYear());
  const [facilityId, setFacilityId] = useState("");
  const [form, setForm] = useState({
    revenue: "",
    revenue_currency: "USD",
    fte_count: "",
    floor_area_m2: "",
    production_units: "",
    production_unit_name: "",
  });
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);

  const load = async () => {
    const params = { year };
    if (facilityId) params.facility_id = facilityId;
    const [s, l] = await Promise.all([
      api.get(`/intensity/summary`, { params }),
      api.get("/intensity", { params: facilityId ? { facility_id: facilityId } : {} }),
    ]);
    setSummary(s.data);
    setList(l.data);
    const row = l.data.find((r) => r.year === year && (r.facility_id || null) === (facilityId || null));
    setForm({
      revenue: row?.revenue ?? "",
      revenue_currency: row?.revenue_currency || "USD",
      fte_count: row?.fte_count ?? "",
      floor_area_m2: row?.floor_area_m2 ?? "",
      production_units: row?.production_units ?? "",
      production_unit_name: row?.production_unit_name || "",
    });
  };

  useEffect(() => { load(); }, [year, facilityId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        year,
        facility_id: facilityId || null,
        revenue_currency: form.revenue_currency,
        revenue: form.revenue === "" ? null : parseFloat(form.revenue),
        fte_count: form.fte_count === "" ? null : parseFloat(form.fte_count),
        floor_area_m2: form.floor_area_m2 === "" ? null : parseFloat(form.floor_area_m2),
        production_units: form.production_units === "" ? null : parseFloat(form.production_units),
        production_unit_name: form.production_unit_name || null,
      };
      await api.put("/intensity", payload);
      toast.success(t("intensitySaved"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const i = summary?.intensities;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Intensity metrics</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("intensity")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="space-y-1.5">
            <Label htmlFor="intensity-facility" data-testid="intensity-facility-label" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
              {t("facilityFilterLabel")}
            </Label>
            <Select value={facilityId || "all"} onValueChange={(v) => setFacilityId(v === "all" ? "" : v)}>
              <SelectTrigger id="intensity-facility" className="w-[220px]" data-testid="intensity-facility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("orgWide")}</SelectItem>
                {facilities.map((f) => (
                  <SelectItem key={`int-fac-${f.id}`} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] font-mono-data" data-testid="intensity-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map((n) => {
                const y = new Date().getFullYear() - n;
                return <SelectItem key={`int-y-${y}`} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">{t("intensityData")}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="intensity-form">
              <div className="space-y-2 col-span-2">
                <Label>{t("revenue")}</Label>
                <div className="flex gap-2">
                  <Input type="number" step="any" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} data-testid="intensity-revenue" />
                  <Input className="w-24" value={form.revenue_currency} onChange={(e) => setForm({ ...form, revenue_currency: e.target.value })} data-testid="intensity-currency" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("fte")}</Label>
                <Input type="number" step="any" value={form.fte_count} onChange={(e) => setForm({ ...form, fte_count: e.target.value })} data-testid="intensity-fte" />
              </div>
              <div className="space-y-2">
                <Label>{t("floorArea")}</Label>
                <Input type="number" step="any" value={form.floor_area_m2} onChange={(e) => setForm({ ...form, floor_area_m2: e.target.value })} data-testid="intensity-area" />
              </div>
              <div className="space-y-2">
                <Label>{t("productionUnits")}</Label>
                <Input type="number" step="any" value={form.production_units} onChange={(e) => setForm({ ...form, production_units: e.target.value })} data-testid="intensity-prod" />
              </div>
              <div className="space-y-2">
                <Label>{t("productionUnitName")}</Label>
                <Input value={form.production_unit_name} onChange={(e) => setForm({ ...form, production_unit_name: e.target.value })} placeholder="e.g. tonnes / units" data-testid="intensity-prod-name" />
              </div>
              <div className="col-span-2">
                <Button type="submit" data-testid="intensity-save">{t("saveIntensity")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Intensity ratios · {year}</CardTitle></CardHeader>
          <CardContent>
            {!summary?.available ? (
              <div className="text-sm text-muted-foreground py-8 text-center">{t("intensityNoData")}</div>
            ) : (
              <div className="space-y-3" data-testid="intensity-summary">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Total emissions</div>
                <div className="font-display text-3xl font-mono-data">{summary.total_t_co2e} <span className="text-base text-muted-foreground">{t("tCO2e")}</span></div>

                {i?.kg_co2e_per_currency != null && (
                  <IntensityRow icon={Ruler} label={t("perRevenue")} value={i.kg_co2e_per_currency} unit={`kgCO₂e / ${summary.revenue_currency}`} testid="ratio-revenue" />
                )}
                {i?.t_co2e_per_fte != null && (
                  <IntensityRow icon={Ruler} label={t("perFte")} value={i.t_co2e_per_fte} unit="tCO₂e / FTE" testid="ratio-fte" />
                )}
                {i?.kg_co2e_per_m2 != null && (
                  <IntensityRow icon={Ruler} label={t("perArea")} value={i.kg_co2e_per_m2} unit="kgCO₂e / m²" testid="ratio-area" />
                )}
                {i?.kg_co2e_per_production_unit != null && (
                  <IntensityRow icon={Ruler} label={`${t("perProduction")} (${summary.production_unit_name || "unit"})`} value={i.kg_co2e_per_production_unit} unit={`kgCO₂e / ${summary.production_unit_name || "unit"}`} testid="ratio-prod" />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {list.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">History</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2">{t("year")}</th>
                    <th className="text-right px-4 py-2">{t("revenue")}</th>
                    <th className="text-right px-4 py-2">{t("fte")}</th>
                    <th className="text-right px-4 py-2">{t("floorArea")}</th>
                    <th className="text-right px-4 py-2">{t("productionUnits")}</th>
                  </tr>
                </thead>
                <tbody className="font-mono-data">
                  {list.map((r) => (
                    <tr key={r.id || `${r.year}-${r.facility_id || 'org'}`} className="border-b border-border/60">
                      <td className="px-4 py-2">{r.year}</td>
                      <td className="px-4 py-2 text-right">{r.revenue ? `${r.revenue.toLocaleString()} ${r.revenue_currency || ""}` : "—"}</td>
                      <td className="px-4 py-2 text-right">{r.fte_count ?? "—"}</td>
                      <td className="px-4 py-2 text-right">{r.floor_area_m2 ?? "—"}</td>
                      <td className="px-4 py-2 text-right">{r.production_units ? `${r.production_units} ${r.production_unit_name || ""}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const IntensityRow = ({ icon: Icon, label, value, unit, testid }) => (
  <div className="flex items-center justify-between border border-border rounded-md p-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon size={16} className="text-primary" />
      <span className="text-sm capitalize">{label}</span>
    </div>
    <div className="text-right">
      <div className="font-display text-lg font-mono-data">{value}</div>
      <div className="text-[10px] text-muted-foreground font-mono-data">{unit}</div>
    </div>
  </div>
);
